import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Smartphone, Activity, Users, AlertTriangle, RefreshCw,
  ArrowUp, ArrowDown, Minus, Wifi, Bell, Shield,
  TrendingUp, Clock, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MobileHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/mobile-health/dashboard?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Mobile health fetch error:', err);
      toast.error('Fehler beim Laden der Mobile-Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="mobile-health-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16 text-gray-500" data-testid="mobile-health-no-data">
        <Smartphone className="w-12 h-12 mx-auto mb-4 opacity-40" />
        <p>Keine Mobile-Daten vorhanden.</p>
      </div>
    );
  }

  const s = data.summary;
  const maxReqs = Math.max(...(data.timeline || []).map(t => t.requests), 1);
  const totalPlatforms = Object.values(data.platforms || {}).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-6" data-testid="mobile-health-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="mobile-health-title">
            <Smartphone className="w-6 h-6 text-amber-600" />
            Mobile App Monitor
          </h1>
          <p className="text-sm text-gray-500 mt-1">API-Nutzung, Geräte und Endpoint-Health</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 14, 30].map((d) => (
            <Button
              key={d}
              variant={days === d ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(d)}
              data-testid={`mobile-period-${d}`}
              className={days === d ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
            >
              {d}T
            </Button>
          ))}
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="mobile-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          title="API-Anfragen"
          value={s.total_requests.toLocaleString()}
          delta={s.total_requests_delta}
          icon={Activity}
          color="blue"
          testId="kpi-requests"
        />
        <KpiCard
          title="Aktive Nutzer"
          value={s.active_users}
          delta={s.active_users_delta}
          icon={Users}
          color="green"
          testId="kpi-active-users"
        />
        <KpiCard
          title="Registrierte Geräte"
          value={s.total_devices}
          icon={Smartphone}
          color="purple"
          testId="kpi-devices"
        />
        <KpiCard
          title="Fehlerrate"
          value={`${s.error_rate}%`}
          delta={s.error_count_delta}
          icon={AlertTriangle}
          color="red"
          testId="kpi-error-rate"
          invertDelta
        />
        <KpiCard
          title="Push-Nutzer"
          value={s.notification_users}
          icon={Bell}
          color="amber"
          testId="kpi-push-users"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Timeline Chart */}
        <Card className="lg:col-span-2" data-testid="mobile-timeline">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              API-Anfragen (24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-[2px] h-36">
              {(data.timeline || []).map((t, i) => (
                <div key={i} className="flex-1 flex flex-col items-center group relative">
                  <div
                    className="w-full rounded-t transition-all bg-blue-400 hover:bg-blue-500 cursor-pointer"
                    style={{ height: `${(t.requests / maxReqs) * 100}%`, minHeight: t.requests > 0 ? '2px' : '0' }}
                    data-testid={`timeline-bar-${i}`}
                  />
                  <div className="absolute -top-8 bg-gray-900 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                    {t.hour}: {t.requests}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-gray-400">
              <span>{data.timeline?.[0]?.hour}</span>
              <span>{data.timeline?.[12]?.hour}</span>
              <span>{data.timeline?.[23]?.hour}</span>
            </div>
          </CardContent>
        </Card>

        {/* Platform Distribution */}
        <Card data-testid="mobile-platforms">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-purple-500" />
              Plattformen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(data.platforms || {}).sort((a, b) => b[1] - a[1]).map(([platform, count]) => {
                const pct = Math.round((count / totalPlatforms) * 100);
                const isIos = platform.toLowerCase().includes('ios');
                return (
                  <div key={platform} data-testid={`platform-${platform}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${isIos ? 'bg-gray-100 text-gray-700' : 'bg-green-50 text-green-700'}`}>
                          {isIos ? 'iOS' : 'And'}
                        </div>
                        <span className="text-sm font-medium text-gray-700 capitalize">{platform}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{count} <span className="text-gray-400 font-normal text-xs">({pct}%)</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${isIos ? 'bg-gray-500' : 'bg-green-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {Object.keys(data.platforms || {}).length === 0 && (
                <p className="text-gray-400 text-sm text-center py-4">Keine Geräte registriert</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Endpoints Table */}
      <Card data-testid="mobile-top-endpoints">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            Top Endpoints
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium text-gray-500">Endpoint</th>
                  <th className="pb-2 font-medium text-gray-500 text-right">Aufrufe</th>
                  <th className="pb-2 font-medium text-gray-500 text-right">Fehler</th>
                  <th className="pb-2 font-medium text-gray-500 text-right">Latenz</th>
                  <th className="pb-2 font-medium text-gray-500 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_endpoints || []).map((ep, i) => {
                  const errorRate = ep.calls > 0 ? ((ep.errors / ep.calls) * 100).toFixed(1) : 0;
                  const isHealthy = errorRate < 5;
                  return (
                    <tr key={i} className="border-b last:border-0 hover:bg-gray-50/60" data-testid={`endpoint-row-${i}`}>
                      <td className="py-2.5 font-mono text-xs text-gray-700 max-w-[280px] truncate">{ep.endpoint}</td>
                      <td className="py-2.5 text-right font-medium">{ep.calls.toLocaleString()}</td>
                      <td className="py-2.5 text-right">
                        <span className={ep.errors > 0 ? 'text-red-500 font-medium' : 'text-gray-400'}>{ep.errors}</span>
                      </td>
                      <td className="py-2.5 text-right">
                        <span className="text-gray-600">{ep.avg_latency}ms</span>
                      </td>
                      <td className="py-2.5 text-right">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isHealthy ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          <Shield className="w-3 h-3" />
                          {isHealthy ? 'OK' : `${errorRate}%`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(data.top_endpoints || []).length === 0 && (
              <p className="text-gray-400 text-sm text-center py-6">Noch keine API-Aufrufe</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Monitored Endpoints */}
      <Card data-testid="mobile-monitored-endpoints">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Wifi className="w-4 h-4 text-green-500" />
            Überwachte Mobile-Endpoints
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {(data.mobile_endpoints || []).map((ep) => (
              <div key={ep} className="flex items-center gap-2 bg-green-50/60 rounded-lg px-3 py-2 text-xs">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                <span className="font-mono text-gray-600 truncate">{ep.replace('/api/', '')}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const KpiCard = ({ title, value, delta, icon: Icon, color, testId, invertDelta = false }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', icon: 'text-blue-500' },
    green: { bg: 'bg-green-50', text: 'text-green-600', icon: 'text-green-500' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', icon: 'text-purple-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', icon: 'text-amber-500' },
    red: { bg: 'bg-red-50', text: 'text-red-600', icon: 'text-red-500' },
  };
  const c = colorMap[color] || colorMap.blue;
  const isPositive = invertDelta ? delta < 0 : delta > 0;
  const isNegative = invertDelta ? delta > 0 : delta < 0;

  return (
    <Card className={`${c.bg}`} data-testid={testId}>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
          <Icon className={`w-4 h-4 ${c.icon}`} />
        </div>
        <p className={`text-2xl font-bold mt-1 ${c.text}`}>{value}</p>
        {delta !== null && delta !== undefined && (
          <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${isPositive ? 'text-emerald-600' : isNegative ? 'text-red-500' : 'text-gray-400'}`} data-testid={`${testId}-delta`}>
            {delta > 0 ? <ArrowUp className="w-3 h-3" /> : delta < 0 ? <ArrowDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            <span>{delta > 0 ? '+' : ''}{delta}%</span>
            <span className="text-gray-400 font-normal ml-0.5">vs. Vorperiode</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MobileHealthPage;
